/**
 * Faculty directory. Generated from a deterministic seed so that every
 * page that references a faculty member resolves the same record.
 */

export interface FacultyDept {
  code: string;
  name: string;
}

export const DEPTS: FacultyDept[] = [
  { code: "CSE", name: "Computer Science & Engineering" },
  { code: "ISE", name: "Information Science & Engineering" },
  { code: "AIML", name: "AI & Machine Learning" },
  { code: "ECE", name: "Electronics & Communication" },
  { code: "EEE", name: "Electrical & Electronics" },
  { code: "MECH", name: "Mechanical Engineering" },
  { code: "CIVIL", name: "Civil Engineering" },
  { code: "BT", name: "Biotechnology" },
  { code: "MBA", name: "Master of Business Administration" },
];

export const DESIGNATIONS = ["Professor & HoD", "Professor", "Associate Professor", "Assistant Professor"];
export const QUALS = ["Ph.D.", "Ph.D., M.Tech", "M.Tech, (Ph.D)", "M.Tech", "Ph.D., M.E."];

export const AREAS: Record<string, string[]> = {
  CSE: ["Machine Learning", "Distributed Systems", "Cybersecurity", "Cloud Computing", "Algorithms", "Data Science", "NLP", "Computer Vision"],
  ISE: ["Big Data", "Web Engineering", "Software Engineering", "DBMS", "Information Retrieval", "IoT"],
  AIML: ["Deep Learning", "Reinforcement Learning", "GenAI", "Computer Vision", "Robotics", "MLOps"],
  ECE: ["VLSI", "Signal Processing", "Antennas", "Embedded Systems", "Wireless Comm.", "Microelectronics"],
  EEE: ["Power Systems", "Renewable Energy", "Control Systems", "Electric Drives", "Smart Grid"],
  MECH: ["Thermal Engineering", "CFD", "Manufacturing", "Materials", "Robotics", "Tribology"],
  CIVIL: ["Structural Engg.", "Geotechnical", "Transportation", "Environmental", "Hydraulics"],
  BT: ["Bioprocess", "Genetic Engg.", "Microbiology", "Biosensors", "Bioinformatics"],
  MBA: ["Marketing", "Finance", "HR", "Operations", "Strategy", "Analytics"],
};

const COUNTS_PER_DEPT = [10, 8, 7, 9, 7, 9, 6, 5, 5];

export interface FacultyMember {
  id: number;
  dept: string;
  deptName: string;
  name: string;
  initials: string;
  designation: string;
  qual: string;
  areas: string[];
  /** Optional rich-profile fields, derived deterministically from id. */
  email: string;
  office: string;
  officeHours: string;
  joined: number;
  bio: string[];
  publications: Array<{ year: number; title: string; venue: string }>;
  courses: string[];
}

function generate(): FacultyMember[] {
  const out: FacultyMember[] = [];
  let id = 1;
  DEPTS.forEach((d, di) => {
    const counts = COUNTS_PER_DEPT[di];
    for (let i = 1; i <= counts; i++) {
      const designation = i === 1 ? DESIGNATIONS[0] : DESIGNATIONS[1 + ((i + di) % 3)];
      const qual = QUALS[(i + di) % QUALS.length];
      const ar = AREAS[d.code];
      const a1 = ar[(i + di) % ar.length];
      const a2 = ar[(i + di + 2) % ar.length];
      const a3 = ar[(i + di + 4) % ar.length];
      const num = String(id).padStart(2, "0");
      const joined = 1985 + ((id * 7) % 38);
      const m: FacultyMember = {
        id,
        dept: d.code,
        deptName: d.name,
        name: "Faculty " + num,
        initials: "F" + (id < 10 ? "0" + id : id),
        designation,
        qual,
        areas: [a1, a2, a3].filter((v, idx, arr) => arr.indexOf(v) === idx),
        email: `faculty${num}@bietdvg.edu`,
        office: `${d.code} Block · Room ${100 + ((id * 3) % 220)}`,
        officeHours: ["Mon-Wed · 11 AM – 12 PM", "Tue + Thu · 3 – 4 PM", "Fri · 10 AM – 12 PM"][id % 3],
        joined,
        bio: [
          `${"Faculty " + num} joined the Department of ${d.name} at BIET in ${joined}, after completing ${qual} with a focus on ${a1.toLowerCase()} and ${a2.toLowerCase()}. Their teaching interests span the foundational courses in the ${d.code} programme, with a sustained research focus on ${a1.toLowerCase()}.`,
          `Their work in ${a1.toLowerCase()} has been published across leading journals, and they currently supervise three doctoral scholars under the VTU-recognised research centre attached to the department. They serve on the Board of Studies for the ${d.code} curriculum and review committee.`,
        ],
        publications: [
          { year: 2025, title: `On adaptive ${a1.toLowerCase()} for resource-constrained settings`, venue: "IEEE Transactions" },
          { year: 2024, title: `Comparative study of ${a2.toLowerCase()} approaches in Indian deployment contexts`, venue: "Springer LNCS" },
          { year: 2023, title: `${a3} for the ${d.name} curriculum: a pedagogical case study`, venue: "Elsevier" },
          { year: 2022, title: `A retrospective on ${a1.toLowerCase()} after a decade of practice`, venue: "ACM" },
        ],
        courses: [
          `${d.code}201 — Foundations of ${a1}`,
          `${d.code}306 — Applied ${a2}`,
          `${d.code}412 — Capstone Studio`,
        ],
      };
      out.push(m);
      id++;
    }
  });
  return out;
}

export const FACULTY: FacultyMember[] = generate();

export function getFaculty(id: number | string): FacultyMember | undefined {
  const n = typeof id === "string" ? parseInt(id, 10) : id;
  if (!Number.isFinite(n)) return undefined;
  return FACULTY.find((f) => f.id === n);
}

export const DESIGNATION_FILTERS = [
  { code: "ALL", label: "All roles" },
  { code: "Professor & HoD", label: "HoDs" },
  { code: "Professor", label: "Professors" },
  { code: "Associate Professor", label: "Associate" },
  { code: "Assistant Professor", label: "Assistant" },
];
